import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Conditional class names, with later Tailwind utilities winning over earlier
 * ones of the same kind. This is the helper `shadcn/ui` components expect to
 * import, so adding one with `pnpm dlx shadcn@latest add <component>` works
 * without editing it.
 *
 * ---------------------------------------------------------------------------
 * ⚠ EVERY PROJECT FONT-SIZE TOKEN MUST BE REGISTERED BELOW, OR IT SILENTLY
 * EATS THE ELEMENT'S TEXT COLOUR
 * ---------------------------------------------------------------------------
 * `tailwind-merge` resolves conflicts by *class group*, and it infers the
 * group from the class name alone — it has never read `styles.css` and cannot.
 * `text-sm` is a font size because the default config lists it; `text-card` is
 * this project's own (`--text-card: 0.9063rem`), so the default config has
 * never heard of it and falls back to the only other thing `text-<name>` can
 * be: a **text colour**. It then drops the real colour as the loser of a
 * conflict that does not exist.
 *
 * `D1`'s screenshot pass found it in the shipped product. `Button
 * variant="primary" size="md"` composes `'bg-accent text-accent-ink'` with
 * `'h-9 px-3 text-card'`, and `cn` returned the second `text-card` and **no
 * `text-accent-ink`** — so every default-size primary button in the product
 * rendered its label in `--color-ink` on `--color-accent`, about **2.7:1**,
 * against the WCAG 2.2 AA floor of 4.5:1 that `FE-13.4` carries by name.
 *
 * Three things made it survive three chunks of primary buttons:
 *
 *  1. **`size="sm"` is unaffected** — `text-sm` IS a size `tailwind-merge`
 *     recognises, so the small buttons have always been correct, and a
 *     side-by-side comparison shows nothing.
 *  2. **Every unit test asserts the class string `cva` produces**, and the
 *     class is dropped *afterwards*, by this function.
 *  3. `styles.tokens.test.ts` measures the `--color-accent-ink` /
 *     `--color-accent` pair and passes — the *tokens* were always right. Only
 *     the delivery was broken.
 *
 * So: **a new `--text-*` token in `styles.css` is a two-file change.** Add it
 * there, register it here, and `cn.twmerge.test.ts` will hold the pair
 * together.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // `--text-card`. Keep this list in step with the `--text-*` tokens in
      // `apps/web/src/styles.css`; there is exactly one today.
      'font-size': [{ text: ['card'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
